/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useRef } from 'react';
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Message } from 'primereact/message';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Skeleton } from 'sharedUi/Skeleton';
import { useBreadcrumbTitle } from 'sharedUi/BreadcrumbContext';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import FormInput from '../../components/forms/form-input';
import FormSelect from '../../components/forms/form-select';
import DiscardChangesDialog from '../../components/dialogs/discard-changes-dialog';
import PermissionGuard from '../../components/guards/PermissionGuard';
import ScreenHeader from '../../components/common/screen-header';
import { JOB_NAMES } from '../../constants/jobNames';
import { districtsService } from '../../services/districts.service';
import { projectsService } from '../../services/projects.service';
import { extractErrorMessage } from '../../utils/error-handler';

/**
 * Validation Schema - Zod
 */
const districtSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'District name is required')
    .regex(
      /^[a-zA-Z\s\-_]+$/,
      'District name may contain only alphabets, spaces, hyphens and underscores'
    )
    .refine(
      (val) => (val.match(/[a-zA-Z]/g) || []).length >= 2,
      'District name must contain at least 2 alphabet characters'
    ),

  // District code: alphanumeric string — letters, digits, hyphens, underscores
  // (matches backend validate_code_string_field). No longer digits-only.
  districtCd: z
    .string()
    .trim()
    .max(50, 'District Code must not exceed 50 characters')
    .regex(/^[a-zA-Z0-9\-_]+$/, 'District Code may contain only letters, digits, hyphens and underscores')
    .optional()
    .or(z.literal('')),

  stateName: z
    .string()
    .trim()
    .min(2, 'State name must be at least 2 characters')
    .regex(/^[a-zA-Z\s]+$/, 'State name may contain only alphabets and spaces')
    .optional()
    .or(z.literal('')),

  appId: z.string().trim().optional().or(z.literal('')),
});

type DistrictFormData = z.infer<typeof districtSchema>;

const DistrictForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({
    entity: 'districts',
    basePath: '/districts',
  });
  const isEditMode = Boolean(id);
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);
  // Stops autosave rewriting the draft after a submit clears it (the reason the
  // "restore?" banner reappeared right after a create/update).
  const submittedRef = useRef(false);

  const DRAFT_KEY = `district-form-draft-${id || 'create'}`;

  // State
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [draftData, setDraftData] = useState<any>(null);

  // Form setup
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<DistrictFormData>({
    resolver: zodResolver(districtSchema) as any,
    defaultValues: {
      name: '',
      districtCd: '',
      stateName: '',
      appId: '',
    },
    mode: 'onChange',
  });

  const formValues = watch();

  // Projects the caller may create in (platform admin = all, else own).
  const { data: accessibleProjects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: projectsService.getAccessible,
    staleTime: 5 * 60 * 1000,
  });
  const projectOptions = accessibleProjects.map((p) => ({
    value: p._id,
    label: p.appCode ? `${p.name} (${p.appCode})` : p.name,
  }));
  const projectLocked = accessibleProjects.length <= 1;
  const selectedAppId = watch('appId');
  useEffect(() => {
    if (!isEditMode && !selectedAppId && accessibleProjects.length) {
      setValue('appId', accessibleProjects[0]._id);
    }
  }, [isEditMode, selectedAppId, accessibleProjects, setValue]);

  // Navigation blocker for unsaved changes
  const { showLeaveDialog, confirmLeave, cancelLeave, handleNavigate } = useNavigationBlocker({ when: isDirty });

  // Fetch district data for edit mode
  const { data: districtData, isLoading: districtLoading } = useQuery({
    queryKey: ['district', id],
    queryFn: () => districtsService.getById(id!),
    enabled: isEditMode && isReady && !!id,
  });

  // Set breadcrumb title to show district name instead of UUID in edit mode
  useBreadcrumbTitle(isEditMode ? districtData?.name : null);

  // Populate form in edit mode
  useEffect(() => {
    if (districtData && isEditMode) {
      reset({
        name: districtData.name || '',
        districtCd: districtData.districtCd || '',
        stateName: districtData.stateName || '',
        appId: (districtData as any).appId || '',
      });
    }
  }, [districtData, isEditMode, reset]);

  // Create/Update mutation
  const mutation = useMutation({
    mutationFn: (data: any) => {
      if (isEditMode && id) {
        return districtsService.update(id, data);
      }
      return districtsService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['districts'] });
      localStorage.removeItem(DRAFT_KEY);

      toast.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: `District ${isEditMode ? 'updated' : 'created'} successfully`,
        life: 3000,
      });

      setTimeout(() => {
        navigateToList();
      }, 1000);
    },
    onError: (error: any) => {
      submittedRef.current = false;
      console.error('Error response:', error.response);

      const errorMessage = extractErrorMessage(
        error,
        `Failed to ${isEditMode ? 'update' : 'create'} district`
      );

      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 10000,
      });
    },
  });

  // Autosave draft
  useEffect(() => {
    if (!isDirty || submittedRef.current) return;

    const timer = setTimeout(() => {
      if (submittedRef.current) return;
      localStorage.setItem(DRAFT_KEY, JSON.stringify(formValues));
    }, 1500);

    return () => clearTimeout(timer);
  }, [formValues, isDirty, DRAFT_KEY]);

  // Load draft on mount
  useEffect(() => {
    if (!isEditMode) {
      const savedDraft = localStorage.getItem(DRAFT_KEY);
      if (savedDraft) {
        try {
          const parsed = JSON.parse(savedDraft);
          setDraftData(parsed);
          setShowDraftBanner(true);
        } catch {
          localStorage.removeItem(DRAFT_KEY);
        }
      }
    }
  }, [isEditMode, DRAFT_KEY]);

  // Handle draft restore
  const handleRestoreDraft = () => {
    if (draftData) {
      reset(draftData);
      setShowDraftBanner(false);
    }
  };

  // Handle draft discard
  const handleDiscardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setShowDraftBanner(false);
    setDraftData(null);
  };

  // Combined loading state for button disable
  const isSaving = mutation.isPending;

  // Form submission
  const onSubmit = async (data: DistrictFormData) => {
    submittedRef.current = true;
    localStorage.removeItem(DRAFT_KEY);
    const payload: { name: string; districtCd?: string; stateName?: string; appId?: string } = {
      name: data.name,
    };

    // Owning project (honoured by the BE only for callers who can access it).
    if (data.appId) payload.appId = data.appId;

    if (data.districtCd && data.districtCd.trim()) {
      payload.districtCd = data.districtCd.trim();
    }

    if (data.stateName && data.stateName.trim()) {
      payload.stateName = data.stateName.trim();
    }

    try {
      await mutation.mutateAsync(payload);
    } catch {
      // Error handled by onError callback
    }
  };

  // Loading state
  if (districtLoading) {
    return (
      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-District-Form">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Skeleton width="40px" height="40px" borderRadius="8px" />
            <div>
              <Skeleton width="200px" height="24px" />
              <Skeleton width="150px" height="16px" className="mt-1" />
            </div>
          </div>
          <div className="flex gap-2">
            <Skeleton width="80px" height="36px" borderRadius="6px" />
            <Skeleton width="80px" height="36px" borderRadius="6px" />
          </div>
        </div>
        <div className="ds-card p-4">
          <Skeleton width="180px" height="20px" className="mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Skeleton height="60px" />
            <Skeleton height="60px" />
            <Skeleton height="60px" />
            <Skeleton height="60px" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <PermissionGuard jobName={JOB_NAMES.DISTRICTS}>
      <Toast ref={toast} position="top-right" />

      <div className="py-4 px-4 lg:px-6 bg-canvas dark:bg-gray-900 min-h-screen" data-testid="SCR-District-Form">
        {/* Header */}
        <ScreenHeader
          icon="pi pi-map"
          title={isEditMode ? 'Edit District' : 'Create New District'}
          subtitle={isEditMode ? 'Update district information' : 'Add a new district to the system'}
          onBack={() => handleNavigate('/districts')}
        />

        {/* Draft Recovery Banner */}
        {showDraftBanner && (
          <div className="mb-4">
            <Message
              severity="info"
              className="w-full"
              content={
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center gap-2">
                    <i className="pi pi-exclamation-circle" style={{ fontSize: '1.125rem' }} />
                    <span>You have unsaved changes from a previous session. Would you like to restore them?</span>
                  </div>
                  <div className="flex gap-2">
                    <Button label="Restore" size="small" severity="info" onClick={handleRestoreDraft} />
                    <Button label="Discard" size="small" severity="secondary" outlined onClick={handleDiscardDraft} />
                  </div>
                </div>
              }
            />
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit as any)} noValidate>
          {/* District Information */}
          <div className="ds-card p-4">
            <div className="flex justify-between items-center mb-4 pb-3 border-b border-line">
              <h3 className="text-sm font-semibold text-ink flex items-center gap-2">
                <i className="pi pi-info-circle text-blue-600" style={{ fontSize: '1rem' }} />
                District Information
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                name="name"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    label="District Name"
                    required
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    autoFocus
                  />
                )}
              />

              <Controller
                name="districtCd"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="District Code"
                    error={!!errors.districtCd}
                    helperText={errors.districtCd?.message}
                    placeholder="Enter district code (e.g. DST-01)"
                  />
                )}
              />

              <Controller
                name="stateName"
                control={control}
                render={({ field }) => (
                  <FormInput
                    {...field}
                    value={field.value || ''}
                    label="State Name"
                    error={!!errors.stateName}
                    helperText={errors.stateName?.message}
                    placeholder="Enter state name"
                  />
                )}
              />

              {/* Owning project */}
              <Controller
                name="appId"
                control={control}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormSelect
                    {...field}
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    label="Project"
                    options={projectOptions}
                    placeholder="Select project"
                    disabled={projectLocked || isEditMode}
                    error={!!errors.appId}
                    helperText={errors.appId?.message}
                  />
                )}
              />
            </div>

            {/* Form Actions */}
            <div className="px-4 py-3 bg-canvas dark:bg-gray-900/50 border-t border-line flex justify-end gap-2 rounded-b-lg">
              <Button
                label="Cancel"
                severity="secondary"
                outlined
                onClick={() => handleNavigate('/districts')}
                disabled={isSaving}
                size="small"
              />
              <Button
                type="button"
                label={isSaving ? 'Saving...' : isEditMode ? 'Update' : 'Create'}
                icon={isSaving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
                disabled={isSaving}
                onClick={handleSubmit(onSubmit as any)}
                size="small"
                testId="DistrictForm.Button.Submit"
              />
            </div>
          </div>
        </form>

        {/* Leave Confirmation Dialog */}
        <DiscardChangesDialog
          visible={showLeaveDialog}
          onStay={cancelLeave}
          onLeave={confirmLeave}
          testId="DistrictForm.Dialog.Leave"
        />
      </div>
    </PermissionGuard>
  );
};

export default DistrictForm;
